import { Settings, User, Bell, Shield, Database, Globe } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback } from "../ui/avatar";

export function AdminConfiguracion() {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl text-[#0F172A] mb-2" style={{ fontWeight: 600 }}>
          Configuración
        </h1>
        <p className="text-gray-600">
          Ajustes generales del sistema y preferencias de administrador
        </p>
      </div>

      <Tabs defaultValue="perfil" className="w-full">
        <TabsList className="mb-6 bg-gray-100 p-1">
          <TabsTrigger value="perfil" className="data-[state=active]:bg-white">
            <User size={16} className="mr-2" />
            Perfil
          </TabsTrigger>
          <TabsTrigger value="notificaciones" className="data-[state=active]:bg-white">
            <Bell size={16} className="mr-2" />
            Notificaciones
          </TabsTrigger>
          <TabsTrigger value="seguridad" className="data-[state=active]:bg-white">
            <Shield size={16} className="mr-2" />
            Seguridad
          </TabsTrigger>
          <TabsTrigger value="sistema" className="data-[state=active]:bg-white">
            <Database size={16} className="mr-2" />
            Sistema
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="perfil" className="space-y-6">
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Información personal</CardTitle>
              <CardDescription>
                Actualiza tu información de perfil y datos de contacto
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarFallback className="bg-[#2B59FF] text-white text-xl">
                    TB
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Button variant="outline" className="border-gray-300 text-gray-700">
                    Cambiar foto
                  </Button>
                  <p className="text-xs text-gray-500">
                    JPG, PNG o GIF. Máximo 2MB.
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre</Label>
                  <Input id="nombre" defaultValue="Tony" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apellidos">Apellidos</Label>
                  <Input id="apellidos" defaultValue="Bellamy" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" defaultValue="tony@bellamypartners.com" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input id="telefono" type="tel" defaultValue="+34 600 123 456" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo</Label>
                <Input id="cargo" defaultValue="CEO & Administrador" />
              </div>

              <Separator />

              <div className="flex gap-3">
                <Button variant="black">
                  Guardar cambios
                </Button>
                <Button variant="outline" className="border-gray-300 text-gray-700">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notificaciones" className="space-y-6">
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Preferencias de notificaciones</CardTitle>
              <CardDescription>
                Configura cómo y cuándo quieres recibir notificaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                {
                  titulo: "Nuevos inversores",
                  descripcion: "Recibir notificación cuando se registre un nuevo inversor",
                  enabled: true
                },
                {
                  titulo: "Nuevos activos",
                  descripcion: "Notificar cuando se agregue un nuevo activo al portfolio",
                  enabled: true
                },
                {
                  titulo: "Documentos pendientes",
                  descripcion: "Alertas sobre documentos que requieren revisión",
                  enabled: true
                },
                {
                  titulo: "Reportes trimestrales",
                  descripcion: "Recordatorios de fechas límite para reportes",
                  enabled: true
                },
                {
                  titulo: "Actualizaciones de KPIs",
                  descripcion: "Notificar cambios significativos en métricas clave",
                  enabled: false
                },
                {
                  titulo: "Resumen semanal",
                  descripcion: "Recibir resumen de actividad cada lunes",
                  enabled: true
                }
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
                  <div className="flex-1">
                    <p className="text-sm text-[#0F172A]" style={{ fontWeight: 500 }}>
                      {item.titulo}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {item.descripcion}
                    </p>
                  </div>
                  <Switch defaultChecked={item.enabled} />
                </div>
              ))}

              <Separator />

              <div className="flex gap-3">
                <Button variant="black">
                  Guardar preferencias
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="seguridad" className="space-y-6">
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Seguridad y acceso</CardTitle>
              <CardDescription>
                Gestiona tu contraseña y opciones de autenticación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="passwordActual">Contraseña actual</Label>
                <Input id="passwordActual" type="password" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordNueva">Nueva contraseña</Label>
                <Input id="passwordNueva" type="password" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="passwordConfirm">Confirmar nueva contraseña</Label>
                <Input id="passwordConfirm" type="password" />
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Autenticación de dos factores
                    </p>
                    <p className="text-sm text-gray-600">
                      Añade una capa extra de seguridad a tu cuenta
                    </p>
                  </div>
                  <Switch />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Inicio de sesión biométrico
                    </p>
                    <p className="text-sm text-gray-600">
                      Usar huella digital o Face ID
                    </p>
                  </div>
                  <Switch />
                </div>
              </div>

              <Separator />

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield size={20} className="text-[#2B59FF] mt-0.5" />
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Sesiones activas
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      2 dispositivos con sesión iniciada
                    </p>
                    <Button variant="link" className="text-[#2B59FF] px-0 mt-2">
                      Ver y gestionar sesiones
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="black">
                  Actualizar contraseña
                </Button>
                <Button variant="outline" className="border-gray-300 text-gray-700">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="sistema" className="space-y-6">
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Configuración del sistema</CardTitle>
              <CardDescription>
                Ajustes generales de la plataforma
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="idioma">Idioma</Label>
                <select 
                  id="idioma" 
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  defaultValue="es"
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Zona horaria</Label>
                <select 
                  id="timezone" 
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  defaultValue="europe/madrid"
                >
                  <option value="europe/madrid">Europa/Madrid (GMT+1)</option>
                  <option value="europe/london">Europa/London (GMT+0)</option>
                  <option value="america/newyork">América/New York (GMT-5)</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="moneda">Moneda predeterminada</Label>
                <select 
                  id="moneda" 
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                  defaultValue="eur"
                >
                  <option value="eur">EUR (€)</option>
                  <option value="usd">USD ($)</option>
                  <option value="gbp">GBP (£)</option>
                </select>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Modo de mantenimiento
                    </p>
                    <p className="text-sm text-gray-600">
                      Desactivar acceso temporal para usuarios
                    </p>
                  </div>
                  <Switch />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Registro de actividad
                    </p>
                    <p className="text-sm text-gray-600">
                      Guardar historial de acciones de usuarios
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Backup automático
                    </p>
                    <p className="text-sm text-gray-600">
                      Realizar copias de seguridad diarias
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>

              <Separator />

              <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <Database size={20} className="text-[#F59E0B] mt-0.5" />
                  <div>
                    <p className="text-sm" style={{ fontWeight: 500 }}>
                      Última copia de seguridad
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      17 de noviembre de 2025, 03:00 AM
                    </p>
                    <Button variant="link" className="text-[#2B59FF] px-0 mt-2">
                      Crear backup manual
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="black">
                  Guardar configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
